import type { Order } from '../../../types/apiResponse/order';
import { defaultColumnsConfig } from './constants/defaultColumnsConfig';
import type { ColumnPart, CustomColumn } from './customColumnTypes';
import { historyColumnFields, readHistoryColumnField } from './historyColumnFields';

export type ColumnField = {
  path: string; label: string; numeric: boolean;
  history?: { column: string; mode: 'sum' | 'latest' | 'distinct'; alias?: keyof Order };
};
export type ColumnEntity = { label: string; fields: ColumnField[] };

const numericOrderFields = new Set([
  'returned_value', 'billed_value', 'Desconto1', 'Desconto2', 'ValorLiquido', 'ValorBruto', 'qtd_itens',
  'aprov_pedmin_vlr', 'ValorComissao', 'PesoPedido', 'RentabilidadePedido',
  'valor_do_frete', 'valor_de_descarga', 'valor_margem_comissao', 'SaldoPedido',
]);
const fields = (prefix: string, entries: [string, string, boolean?][]): ColumnField[] => entries.map(
  ([key, label, numeric = false]) => ({ path: `${prefix}.${key}`, label, numeric }),
);

// Explicit catalog: no traversal of arbitrary records, collections or executable expressions.
export const columnEntities: ColumnEntity[] = [
  {
    label: 'Pedido',
    fields: defaultColumnsConfig.filter((column) => !column.subItems.length).map((column) => ({
      path: column.key, label: column.label, numeric: numericOrderFields.has(column.key),
    })),
  },
  { label: 'Histórico pedido', fields: historyColumnFields },
  { label: 'Cliente', fields: fields('customer', [
    ['CodigoCliente', 'Código'], ['RazaoSocial', 'Razão social'], ['CGCCPF', 'CPF / CNPJ'],
    ['CodigoCanalCliente', 'Código do canal'], ['channel_description', 'Descrição do canal'],
    ['credit_limit.LimiteCredito', 'Limite de crédito', true],
  ]) },
  { label: 'Vendedor', fields: fields('seller', [
    ['CodigoVendedor', 'Código'], ['Nome', 'Nome'], ['Telefone', 'Telefone'],
    ['Email', 'E-mail'], ['CodigoFilial', 'Código da filial'],
  ]) },
  { label: 'Unidade de faturamento', fields: fields('billing_unit', [
    ['CodigoUnidFat', 'Código'], ['DescricaoUnidFat', 'Descrição'], ['UF', 'UF'],
  ]) },
  { label: 'Forma de pagamento', fields: fields('payment_method', [
    ['Codigo', 'Código'], ['Descricao', 'Descrição'],
  ]) },
  { label: 'Condição de pagamento', fields: fields('paymentcondition', [
    ['Codigo', 'Código'], ['Descricao', 'Descrição'],
  ]) },
  { label: 'Tipo de pedido', fields: fields('order_type', [
    ['CodigoTipoPed', 'Código'], ['DescricaoTipoPed', 'Descrição'],
  ]) },
  { label: 'ERP', fields: fields('erp_info', [
    ['ErpNumeroPedido', 'Número do pedido'], ['ExportadoErp', 'Exportado'],
    ['ExportadoErpErro', 'Mensagem de exportação'],
  ]) },
];

const catalog = new Map(columnEntities.flatMap((entity) => entity.fields.map(
  (field) => [field.path, { ...field, entity: entity.label }] as const,
)));
export const getColumnField = (path: string) => catalog.get(path);

export function readColumnField(order: Order, path: string, numeric = false): unknown {
  const field = catalog.get(path);
  if (!field) throw new Error('Campo indisponível.');
  if (field.history) return readHistoryColumnField(order, field, numeric);
  return path.split('.').reduce<unknown>((value, key) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)
      || !Object.prototype.hasOwnProperty.call(value, key)) return undefined;
    return (value as Record<string, unknown>)[key];
  }, order);
}

type MathToken = { kind: 'value'; value: number } | { kind: 'operator'; value: string };

function mathTokens(parts: ColumnPart[], order?: Order): MathToken[] {
  const tokens: MathToken[] = [];
  // Coalesce text so a persisted definition cannot bypass validation via split strings.
  const merged: ColumnPart[] = [];
  parts.forEach((part) => {
    const previous = merged[merged.length - 1];
    if (part.type === 'text' && previous?.type === 'text') previous.value += part.value;
    else merged.push({ ...part });
  });
  merged.forEach((part) => {
    if (part.type === 'field') {
      if (!catalog.get(part.path)?.numeric) throw new Error('Escolha um campo numérico.');
      let value = 1;
      if (order) {
        const raw = readColumnField(order, part.path, true);
        if (raw === null || raw === undefined || (typeof raw === 'string' && !raw.trim())
          || (typeof raw !== 'number' && typeof raw !== 'string')) {
          throw new Error('Um dos campos está sem valor numérico.');
        }
        // API decimals use a dot. Never guess thousands separators or coerce booleans.
        value = Number(raw);
        if (!Number.isFinite(value)) throw new Error('Um dos campos não é um número válido.');
      }
      tokens.push({ kind: 'value', value });
    } else if (part.type === 'text') {
      const pieces = part.value.match(/\d+(?:[.,]\d*)?|[.,]\d+|[+*/-]/g) || [];
      if (pieces.join('') !== part.value) {
        throw new Error('Use somente números e os operadores + − × ÷, sem espaços.');
      }
      pieces.forEach((piece) => tokens.push(/^[+*/-]$/.test(piece)
        ? { kind: 'operator', value: piece }
        : { kind: 'value', value: Number(piece.replace(',', '.')) }));
    } else throw new Error('Valor inválido.');
  });
  tokens.forEach((token, index) => {
    if (token.kind !== (index % 2 === 0 ? 'value' : 'operator')) {
      throw new Error('Alterne números ou campos com um operador. Não comece com um operador.');
    }
  });
  return tokens;
}

export function validateCustomColumn(definition: CustomColumn, partial = false): string {
  try {
    if (definition.version !== 1 || !Array.isArray(definition.parts)
      || definition.parts.length > 200) return 'Valor inválido ou muito longo.';
    if (definition.type !== 'text' && definition.type !== 'number') return 'Tipo inválido.';
    definition.parts.forEach((part) => {
      if (part.type === 'field' && !catalog.has(part.path)) throw new Error('Campo indisponível.');
      if (part.type === 'text' && (typeof part.value !== 'string' || part.value.length > 2000)) {
        throw new Error('Texto inválido ou muito longo.');
      }
      if (part.type !== 'text' && part.type !== 'field') throw new Error('Valor inválido.');
    });
    if (definition.type === 'number') {
      const tokens = mathTokens(definition.parts);
      if (!partial && (!tokens.length || tokens[tokens.length - 1].kind !== 'value')) {
        return 'Complete o cálculo com um número ou campo, sem operador no final.';
      }
    } else if (!partial && !definition.parts.some((part) => part.type === 'field' || part.value.trim())) {
      return 'Adicione um campo ou escreva um texto para a coluna.';
    }
    return '';
  } catch (error) {
    return error instanceof Error ? error.message : 'Não foi possível ler este valor.';
  }
}

export function evaluateCustomColumn(order: Order, definition: CustomColumn): { value: string | number; error?: string } {
  try {
    const invalid = validateCustomColumn(definition);
    if (invalid) throw new Error(invalid);
    if (definition.type === 'text') {
      const value = definition.parts.map((part) => {
        if (part.type === 'text') return part.value;
        const raw = readColumnField(order, part.path);
        if (raw === null || raw === undefined) return '—';
        if (typeof raw === 'boolean') return raw ? 'Sim' : 'Não';
        if (typeof raw !== 'string' && typeof raw !== 'number') throw new Error('Campo indisponível.');
        return String(raw);
      }).join('');
      return { value };
    }
    const tokens = mathTokens(definition.parts, order);
    // Two passes preserve multiplication/division precedence, without eval or Function.
    const terms: number[] = [];
    const sums: string[] = [];
    let current = (tokens[0] as { value: number }).value;
    for (let index = 1; index < tokens.length; index += 2) {
      const operator = tokens[index].value;
      const next = tokens[index + 1].value as number;
      if (operator === '*') current *= next;
      else if (operator === '/') {
        if (next === 0) throw new Error('Não é possível dividir por zero.');
        current /= next;
      } else {
        terms.push(current);
        sums.push(String(operator));
        current = next;
      }
    }
    terms.push(current);
    const value = terms.slice(1).reduce((total, term, index) => (
      sums[index] === '+' ? total + term : total - term
    ), terms[0]);
    if (!Number.isFinite(value)) throw new Error('O resultado não é um número válido.');
    return { value };
  } catch (error) {
    return { value: 'Erro', error: error instanceof Error ? error.message : 'Não foi possível calcular.' };
  }
}
